import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api, { API, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { ColorField, SaveButton, Section } from "./_shared";

export default function AppearancePanel({ brand, onUpdated }) {
  const { t, lang, setLang, languages, addLanguage } = useI18n();
  const { updateProfile } = useAuth();
  const [savingLang, setSavingLang] = useState(false);
  const [pendingLang, setPendingLang] = useState(lang);
  const [newLang, setNewLang] = useState("");
  const [sendingLangRequest, setSendingLangRequest] = useState(false);

  // Workspace avatar — same field (`brand.logo_file_id`) that drives the
  // hub-overview card and the browser-tab favicon. Owners and Managers only.
  const canEditAvatar = brand.role === "owner" || brand.role === "manager";
  const [uploading, setUploading] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const avatarRef = useRef(null);
  const avatarUrl = brand.logo_file_id ? fileUrl(brand.logo_file_id) : null;
  const hasAvatar = !!avatarUrl;

  const uploadAvatarFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) {
      toast.error(t("settings.workspace_avatar_bad_type"));
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("bh_token");
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${API}/upload?brand_id=${brand.id}`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) throw new Error("upload failed");
      const { file_id } = await resp.json();
      await api.put(`/brands/${brand.id}`, { logo_file_id: file_id });
      toast.success(t("settings.workspace_avatar_uploaded"));
      setShowUploader(false);
      onUpdated?.();
    } catch { toast.error(t("settings.workspace_avatar_upload_failed")); }
    finally {
      setUploading(false);
      if (avatarRef.current) avatarRef.current.value = "";
    }
  };

  const [colors, setColors] = useState({
    color_header_bg: brand.color_header_bg || "#ADD3FF",
    color_accent: brand.color_accent || "#A78BFA",
  });
  const initialColors = {
    color_header_bg: brand.color_header_bg || "#ADD3FF",
    color_accent: brand.color_accent || "#A78BFA",
  };
  const colorsDirty =
    colors.color_header_bg !== initialColors.color_header_bg ||
    colors.color_accent !== initialColors.color_accent;
  useEffect(() => {
    setColors({
      color_header_bg: brand.color_header_bg || "#ADD3FF",
      color_accent: brand.color_accent || "#A78BFA",
    });
  }, [brand.id, brand.color_header_bg, brand.color_accent]);

  useEffect(() => { setPendingLang(lang); }, [lang]);

  const saveLanguage = async () => {
    if (pendingLang === lang) return;
    setSavingLang(true);
    try {
      await updateProfile({ language: pendingLang });
      setLang(pendingLang);
      toast.success(t("toasts.saved"));
    } catch { toast.error(t("toasts.save_failed")); }
    finally { setSavingLang(false); }
  };

  const requestLanguage = async () => {
    const value = newLang.trim();
    if (!value) { toast.error(t("toasts.language_request_empty")); return; }
    // Accept input in two shapes:
    //   1) "fr" / "fr-FR"  — ISO code only
    //   2) "fr: Français" / "French" — code + name OR just a native name
    let code, name;
    const colon = value.split(":");
    if (colon.length === 2) {
      code = colon[0].trim().toLowerCase();
      name = colon[1].trim();
    } else if (/^[a-z]{2}(-[a-z]{2})?$/i.test(value)) {
      code = value.toLowerCase();
      name = value;
    } else {
      code = value.slice(0, 2).toLowerCase();
      name = value;
    }
    setSendingLangRequest(true);
    try {
      const data = await addLanguage(code, name);
      await updateProfile({ language: data.lang_code || code });
      toast.success(t("toasts.auto_translate_success"));
      setNewLang("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toasts.auto_translate_failed"));
    } finally { setSendingLangRequest(false); }
  };

  const saveColors = async () => {
    try {
      await api.put(`/brands/${brand.id}`, colors);
      toast.success(t("toasts.saved"));
      onUpdated?.();
    } catch { toast.error(t("toasts.save_failed")); }
  };

  return (
    <>
      <Section
        title={t("settings.appearance_language_title")}
        subtitle={t("settings.appearance_language_sub")}
        action={<SaveButton onClick={saveLanguage} testid="appearance-save-language" label={t("common.save_changes")} disabled={savingLang} dirty={pendingLang !== lang} />}
      >
        <div>
          <Label className="text-[13px] text-[#1a1a1a]">{t("settings.appearance_language_question")}</Label>
          <select
            data-testid="appearance-language-select"
            value={pendingLang}
            onChange={(e) => setPendingLang(e.target.value)}
            disabled={savingLang}
            className="bh-select mt-1.5 w-full h-11 bg-white border border-gray-200 rounded-md px-3 text-sm focus:outline-none focus:border-[var(--bh-accent,#fe7f57)]"
          >
            {languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>

        <div className="pt-5 mt-5 border-t border-gray-100">
          <Label className="text-[13px] text-[#1a1a1a]">{t("settings.appearance_language_request_title")}</Label>
          <p className="text-xs text-gray-500 mt-0.5 mb-2">{t("settings.appearance_language_request_sub")}</p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                data-testid="appearance-language-request-input"
                value={newLang}
                onChange={(e) => setNewLang(e.target.value)}
                placeholder={t("settings.appearance_language_request_placeholder")}
                className="h-11 bg-white border-gray-200"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); requestLanguage(); } }}
              />
            </div>
            <Button
              data-testid="appearance-language-request-submit"
              onClick={requestLanguage}
              disabled={sendingLangRequest}
              className="h-11 rounded-lg bg-[#1a1a1a] text-white hover:bg-black/90 shrink-0 px-4"
            >
              {t("settings.appearance_language_request_submit")}
            </Button>
          </div>
        </div>
      </Section>

      <Section
        title={t("settings.appearance_avatar_title")}
        subtitle={t("settings.appearance_avatar_sub")}
      >
        <div className="flex items-stretch gap-3">
          <div
            className="w-24 h-24 rounded-md shrink-0 overflow-hidden"
            style={{ background: avatarUrl ? `center/cover no-repeat url('${avatarUrl}')` : (brand.color_accent ? brand.color_accent : "linear-gradient(135deg,#ff8ab1 0%,#fecbc6 20%,#fef067 45%,#60d394 70%,#4cc9f0 100%)") }}
            data-testid="workspace-avatar-preview"
          />
          {!canEditAvatar ? (
            <div className="flex-1 min-w-0 flex items-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-md px-4 bg-gray-50/50">
              {t("settings.workspace_avatar_viewer_hint")}
            </div>
          ) : hasAvatar && !showUploader ? (
            <div className="flex items-center">
              <Button
                onClick={() => setShowUploader(true)}
                data-testid="workspace-avatar-choose-another"
                className="h-10 rounded-md bg-white border border-gray-200 text-slate-900 hover:bg-gray-50 shadow-none text-sm"
              >
                <Upload className="w-4 h-4 mr-2" /> {t("settings.workspace_avatar_choose_another")}
              </Button>
            </div>
          ) : (
            <div
              role="button" tabIndex={0}
              onClick={() => !uploading && avatarRef.current?.click()}
              onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) { e.preventDefault(); avatarRef.current?.click(); } }}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setIsDragging(true); }}
              onDragEnter={(e) => { e.preventDefault(); if (!uploading) setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); uploadAvatarFile(e.dataTransfer?.files?.[0]); }}
              data-testid="workspace-avatar-upload-zone"
              className={`flex-1 min-w-0 flex items-center justify-center gap-2 rounded-md border border-dashed px-4 cursor-pointer transition ${uploading ? "opacity-60 cursor-wait" : ""} ${isDragging ? "border-slate-900 bg-gray-100" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
            >
              <Upload className="w-4 h-4 text-gray-500 pointer-events-none" />
              <div className="text-sm text-[#1a1a1a] pointer-events-none">{isDragging ? t("settings.workspace_avatar_drop_release") : t("settings.workspace_avatar_drop_prompt")}</div>
              <div className="text-xs text-gray-500 pointer-events-none">{t("settings.workspace_avatar_hint")}</div>
            </div>
          )}
          <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadAvatarFile(e.target.files?.[0])} />
        </div>
      </Section>

      <Section
        title={t("settings.appearance_colors_title")}
        subtitle={t("settings.appearance_colors_sub")}
        action={<SaveButton onClick={saveColors} testid="appearance-save-colors" label={t("common.save_changes")} dirty={colorsDirty} />}
      >
        <ColorField
          label={t("settings.appearance_header_bg")}
          color={colors.color_header_bg}
          onChange={(v) => setColors((c) => ({ ...c, color_header_bg: v }))}
          testid="color-header-bg"
        />
        <ColorField
          label={t("settings.appearance_accent")}
          color={colors.color_accent}
          onChange={(v) => setColors((c) => ({ ...c, color_accent: v }))}
          testid="color-accent"
        />
      </Section>
    </>
  );
}
